import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { User, Mail, Calendar, Activity, Target, Save, Edit2, Download, FileText, Watch, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { exportGlucoseToPDF, exportGlucoseToCSV, exportMealsToPDF, exportMealsToCSV, exportFullReport } from '../utils/exportData';
import WearableConnect from '../components/WearableConnect';
import api from '../services/api';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    age: user?.age || '',
    diabetes_type: user?.diabetes_type || 'Type 2',
    target_glucose_min: user?.target_glucose_min || 80,
    target_glucose_max: user?.target_glucose_max || 130
  });

  const handleChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updateUser({
        name: formData.name,
        age: formData.age ? parseInt(formData.age) : null,
        diabetes_type: formData.diabetes_type,
        target_glucose_min: parseInt(formData.target_glucose_min),
        target_glucose_max: parseInt(formData.target_glucose_max)
      });
      
      if (result.success) {
        toast({
          title: "Profile Updated",
          description: "Your profile has been saved successfully."
        });
        setEditing(false);
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to update profile",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update profile",
        variant: "destructive"
      });
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Profile Settings</h2>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Manage your account and preferences</p>
      </div>

      {/* Profile Card */}
      <Card className="shadow-md border-0 dark:bg-gray-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-blue-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div>
                <CardTitle className="text-xl dark:text-white">{user?.name || 'User'}</CardTitle>
                <CardDescription className="dark:text-gray-400">{user?.email}</CardDescription>
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={() => setEditing(!editing)}
              className="dark:border-gray-600 dark:text-gray-300"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              {editing ? 'Cancel' : 'Edit'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <User className="w-5 h-5" />
              Personal Information
            </h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="dark:text-gray-300">Full Name</Label>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  {editing ? (
                    <Input
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  ) : (
                    <span className="text-gray-900 dark:text-white">{user?.name}</span>
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="dark:text-gray-300">Email</Label>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-900 dark:text-white">{user?.email}</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="dark:text-gray-300">Age</Label>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  {editing ? (
                    <Input
                      type="number"
                      value={formData.age}
                      onChange={(e) => handleChange('age', e.target.value)}
                      className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Enter your age"
                    />
                  ) : (
                    <span className="text-gray-900 dark:text-white">{user?.age || 'Not set'}</span>
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="dark:text-gray-300">Diabetes Type</Label>
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-gray-400" />
                  {editing ? (
                    <Select
                      value={formData.diabetes_type}
                      onValueChange={(value) => handleChange('diabetes_type', value)}
                    >
                      <SelectTrigger className="dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="dark:bg-gray-700">
                        <SelectItem value="Type 1">Type 1</SelectItem>
                        <SelectItem value="Type 2">Type 2</SelectItem>
                        <SelectItem value="Gestational">Gestational</SelectItem>
                        <SelectItem value="Pre-diabetes">Pre-diabetes</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="text-gray-900 dark:text-white">{user?.diabetes_type || 'Not set'}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Glucose Target Range */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Target className="w-5 h-5" />
              Glucose Target Range
            </h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="dark:text-gray-300">Minimum (mg/dL)</Label>
                {editing ? (
                  <Input
                    type="number"
                    value={formData.target_glucose_min}
                    onChange={(e) => handleChange('target_glucose_min', e.target.value)}
                    className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                ) : (
                  <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                    {user?.target_glucose_min || 80} mg/dL
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label className="dark:text-gray-300">Maximum (mg/dL)</Label>
                {editing ? (
                  <Input
                    type="number"
                    value={formData.target_glucose_max}
                    onChange={(e) => handleChange('target_glucose_max', e.target.value)}
                    className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                ) : (
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {user?.target_glucose_max || 130} mg/dL
                  </p>
                )}
              </div>
            </div>
            
            {!editing && (
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Your target glucose range is <span className="font-semibold text-gray-900 dark:text-white">
                  {user?.target_glucose_min || 80} - {user?.target_glucose_max || 130} mg/dL
                  </span>. Readings within this range will be marked as "in range" in your logs.
                </p>
              </div>
            )}
          </div>

          {/* Save Button */}
          {editing && (
            <div className="flex justify-end gap-2 pt-4 border-t dark:border-gray-700">
              <Button 
                variant="outline" 
                onClick={() => setEditing(false)}
                className="dark:border-gray-600 dark:text-gray-300"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSave}
                className="bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600"
                disabled={saving}
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card className="shadow-md border-0 dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="dark:text-white">Account Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <p className="text-gray-600 dark:text-gray-400">
              Member since: {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : 'Unknown'}
            </p>
            <p className="text-gray-600 dark:text-gray-400">
              Account ID: {user?.id?.slice(0, 8)}...
            </p>
            <p className="text-gray-600 dark:text-gray-400">
              Subscription: <span className="font-medium text-emerald-600 dark:text-emerald-400 capitalize">{user?.subscription || 'Free'}</span>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Export Data Section */}
      <ExportDataCard user={user} />

      {/* Wearable Devices Section */}
      <Card className="shadow-md border-0 dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="dark:text-white flex items-center gap-2">
            <Watch className="w-5 h-5" />
            Wearable Devices
          </CardTitle>
        </CardHeader>
        <CardContent>
          <WearableConnect />
        </CardContent>
      </Card>
    </div>
  );
};

// Export Data Card Component
const ExportDataCard = ({ user }) => {
  const [exporting, setExporting] = useState(null);

  const handleExport = async (type, format) => {
    setExporting(`${type}-${format}`);
    try {
      let data;
      
      if (type === 'glucose') {
        const response = await api.get('/glucose');
        data = response.data.logs || response.data || [];
      } else if (type === 'meals') {
        const response = await api.get('/meals');
        data = response.data.meals || response.data || [];
      }

      if (!data || data.length === 0) {
        toast({
          title: "No Data",
          description: `No ${type} data available to export.`,
          variant: "destructive"
        });
        setExporting(null);
        return;
      }

      if (format === 'pdf') {
        if (type === 'glucose') {
          exportGlucoseToPDF(data, user?.name || 'User');
        } else {
          exportMealsToPDF(data, user?.name || 'User');
        }
      } else {
        if (type === 'glucose') {
          exportGlucoseToCSV(data);
        } else {
          exportMealsToCSV(data);
        }
      }

      toast({
        title: "Export Complete",
        description: `Your ${type} data has been exported successfully.`
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export Failed",
        description: "Failed to export data. Please try again.",
        variant: "destructive"
      });
    }
    setExporting(null);
  };

  const handleFullReport = async () => {
    setExporting('full-report');
    try {
      const [glucoseRes, mealsRes, medsRes] = await Promise.all([
        api.get('/glucose').catch(() => ({ data: { logs: [] } })),
        api.get('/meals').catch(() => ({ data: { meals: [] } })),
        api.get('/medications').catch(() => ({ data: { medications: [] } }))
      ]);

      await exportFullReport(
        glucoseRes.data.logs || glucoseRes.data || [],
        mealsRes.data.meals || mealsRes.data || [],
        medsRes.data.medications || medsRes.data || [],
        user?.name || 'User'
      );

      toast({
        title: "Full Report Generated",
        description: "Your complete health report has been downloaded."
      });
    } catch (error) {
      console.error('Full report error:', error);
      toast({
        title: "Report Failed",
        description: "Failed to generate full report.",
        variant: "destructive"
      });
    }
    setExporting(null);
  };

  return (
    <Card className="shadow-md border-0 dark:bg-gray-800">
      <CardHeader>
        <CardTitle className="dark:text-white flex items-center gap-2">
          <Download className="w-5 h-5" />
          Export Your Data
        </CardTitle>
        <CardDescription className="dark:text-gray-400">
          Download your health data for personal records or to share with your healthcare provider
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Blood Sugar Export */}
        <div className="flex items-center justify-between p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
          <div className="flex items-center gap-3">
            <Activity className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Blood Sugar Data</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">All glucose readings and trends</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleExport('glucose', 'csv')}
              disabled={exporting === 'glucose-csv'}
              className="dark:border-gray-600"
            >
              {exporting === 'glucose-csv' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'CSV'}
            </Button>
            <Button
              size="sm"
              onClick={() => handleExport('glucose', 'pdf')}
              disabled={exporting === 'glucose-pdf'}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {exporting === 'glucose-pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'PDF'}
            </Button>
          </div>
        </div>

        {/* Meals Export */}
        <div className="flex items-center justify-between p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Meal Data</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">All meal logs with nutrition info</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleExport('meals', 'csv')}
              disabled={exporting === 'meals-csv'}
              className="dark:border-gray-600"
            >
              {exporting === 'meals-csv' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'CSV'}
            </Button>
            <Button
              size="sm"
              onClick={() => handleExport('meals', 'pdf')}
              disabled={exporting === 'meals-pdf'}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {exporting === 'meals-pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'PDF'}
            </Button>
          </div>
        </div>

        {/* Full Report */}
        <div className="pt-4 border-t dark:border-gray-700">
          <Button
            onClick={handleFullReport}
            disabled={exporting === 'full-report'}
            className="w-full bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600"
          >
            {exporting === 'full-report' ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating Report...</>
            ) : (
              <><Download className="w-4 h-4 mr-2" /> Download Full Health Report (PDF)</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default Profile;
